/* eslint-disable react/no-unused-state */
import React from 'react'
import { has } from 'lodash'

export interface LanguageType {
  name: string
  slug: string
}

// Have to give these index signatures so we can index into
// them with arbitrary strings
export const languageNames: { [key: string]: string } = {
  Arabic: 'ar',
  Chinese: 'zh-tw',
  English: 'en',
  French: 'fr',
  German: 'de',
  Indonesian: 'id',
  Italian: 'it',
  Japanese: 'ja',
  Mandarin: 'zh-cn',
  Polish: 'pl',
  Portuguese: 'pt',
  Russian: 'ru',
  Spanish: 'es'
}
export const languageSlugs: { [key: string]: string } = {
  ar: 'Arabic',
  'zh-tw': 'Chinese',
  en: 'English',
  fr: 'French',
  de: 'German',
  id: 'Indonesian',
  it: 'Italian',
  ja: 'Japanese',
  'zh-cn': 'Mandarin',
  pl: 'Polish',
  pt: 'Portuguese',
  ru: 'Russian',
  es: 'Spanish'
}

export const defaultLanguage = 'en'

interface Props {
  initialLanguage?: string
}

interface State {
  language: string // One of the slugs in languageSlugs above
  updateLanguage: (language: string) => void
}

/**
 * A React context provider for components that depend on the language of the page.
 *
 * Represents only the language of the current page, which may not be the same
 * as the language the user has selected in the personalization menu. For
 * example, if the user has selected Spanish as their preferred language, but
 * navigates to a /entity/zh-tw/ entity page, the language should be set to
 * 'zh-tw' by the entity page's <main> component
 */
export class LocalizationProvider extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.updateLanguage = this.updateLanguage.bind(this)

    this.state = {
      language: props.initialLanguage || defaultLanguage,
      updateLanguage: this.updateLanguage
    }
  }

  /**
   * Set the current language for this context. If an invalid language is
   * passed in, set the language to the default (English)
   */
  updateLanguage(languageSlug: string) {
    if (has(languageSlugs, languageSlug)) {
      this.setState({
        language: languageSlug
      })
    } else {
      this.setState({ language: defaultLanguage })
    }
  }

  render() {
    /* eslint-disable-next-line react/prop-types */
    const { children } = this.props
    return (
      <LocalizationContext.Provider value={this.state}>
        {children}
      </LocalizationContext.Provider>
    )
  }
}

export const LocalizationContext = React.createContext<State>({
  language: defaultLanguage,
  updateLanguage: () => null
})
